import { Checkbox, Chip, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import { FormikCustomMultiAutocompleteField } from '@components/FormikFields';
import Icon from '@components/Icon/Icon';

import type { UserOption } from '@models/alert';

import { formatName } from '@utils/format';

import {
  useListAlertRecipientSettingsLazyQuery,
  type ListAlertRecipientSettingsQuery,
} from 'generated/alert_recipient_setting.generated';
import { AlertRecipientType } from 'generated/types';

interface UserAlertSettingAutocompleteProps {
  account_id: string;
  label?: string;
  name: string;
  required?: boolean;
}

export default function UserAlertSettingAutocomplete({
  account_id,
  label,
  name,
  required,
}: UserAlertSettingAutocompleteProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [recipients, setRecipients] = useState<
    ListAlertRecipientSettingsQuery['listAlertRecipientSettings']
  >([]);
  const [getRecipients, { loading }] = useListAlertRecipientSettingsLazyQuery();

  const handleOpen = () => {
    setOpen(true);
    (async () => {
      const { data: recipientsData } = await getRecipients({
        variables: {
          input: {
            account_id,
            recipient_type: AlertRecipientType.user,
          },
        },
      });
      setRecipients(recipientsData?.listAlertRecipientSettings ?? []);
    })();
  };

  const handleClose = () => {
    setOpen(false);
    setRecipients([]);
  };

  const options = recipients.map<UserOption>(
    ({ account_id, user_id, account, user }) => {
      const name = user
        ? formatName(user.first_name, user.last_name)
        : account
          ? account.account_name
          : '';
      return {
        user_id,
        account_id,
        name: name ?? '',
      };
    }
  );

  return (
    <FormikCustomMultiAutocompleteField
      async={{
        loading,
        onOpen: handleOpen,
        onClose: handleClose,
        open,
      }}
      disableCloseOnSelect
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
      label={label}
      name={name}
      options={options}
      required={required}
      renderOption={(props, option, { selected }) => {
        const { name, user_id } = option;
        return (
          <li {...props} key={`alert-recipient-setting-option-${user_id}`}>
            <Checkbox
              checked={selected}
              checkedIcon={<Icon name="square-check" size="xl" type="fas" />}
              icon={<Icon name="square" size="xl" type="far" />}
              style={{ marginRight: 8 }}
            />
            <Stack spacing={0}>
              <Typography>{name}</Typography>
              {/** TODO: Display User/Organization? Display email &/or phone #? */}
              <Typography variant="caption">{name}</Typography>
            </Stack>
          </li>
        );
      }}
      renderTags={(value, getTagProps) => (
        <>
          {value.map((option, index) => {
            const { name, user_id } = option;
            return (
              <Chip
                {...getTagProps({ index })}
                key={`selected-alert-recipient-setting-${user_id}`}
                label={
                  <Typography
                    sx={{
                      color: (theme) => theme.palette.text.primary,
                      textTransform: 'capitalize',
                    }}
                    variant="caption"
                  >
                    {name}
                  </Typography>
                }
              />
            );
          })}
        </>
      )}
    />
  );
}
